import { EventContentArg, EventInput } from '@fullcalendar/core';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useComplianceCalendarEvents } from '@monorepo/shared/hooks/complianceCalendar/useComplianceCalendarEvents';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import { useIsLoadingSlow } from '@monorepo/shared/hooks/useIsLoadingSlow';
import { navigateWithReferrer } from '@monorepo/shared/utils/navigation/navigationReferrer';
import {
  ComplianceCalendarEvent,
  ComplianceCalendarEventStatus,
  ComplianceCalendarEventType,
} from 'mapistry-shared';
import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarEvent } from './CalendarEvent';
import { CalendarEventTooltip } from './CalendarEventTooltip';
import {
  ComplianceCalendarProvider,
  useComplianceCalendarContext,
} from './ComplianceCalendarContext';
import { ComplianceCalendarErrorFallback } from './ComplianceCalendarErrorFallback';
import {
  ComplianceCalendarHeader,
  getCalendarRange,
} from './ComplianceCalendarHeader';
import {
  CalendarViewType,
  convertEventToFullCalendarFormat,
  createLoadingEvents,
  exclusiveToInclusiveRange,
  filterCalendarEvents,
  getEventLinkProps,
  isEventClickable,
} from './helpers/complianceCalendarHelpers';
import { useHasOutrunFetchBuffer } from './hooks/useHasOutrunFetchBuffer';
import { useIsInViewport } from './hooks/useIsInViewport';
import { ComplianceCalendarListView } from './ListView/ComplianceCalendarListView';
import { ComplianceCalendarMonthView } from './MonthView/ComplianceCalendarMonthView';
import { CalendarRoot, MonthViewEventLink, StyledSkeleton } from './styled';
import { ComplianceCalendarWeekView } from './WeekView/ComplianceCalendarWeekView';

function ComplianceCalendarContent() {
  const { project } = useCurrentProject();
  const navigate = useNavigate();
  const {
    filters,
    goToToday,
    view,
    visibleRangeExclusiveEnd,
    visibleRangeStart,
  } = useComplianceCalendarContext();

  // used for scrolling to today in list
  const listScrollRef = useRef<HTMLDivElement>(null);
  const {
    setTargetRef: setTodaySectionRef,
    target: todaySectionEl,
    isInViewport: isTodaySectionInViewport,
  } = useIsInViewport({ root: listScrollRef });

  const goToTodayInList = useCallback(() => {
    if (todaySectionEl) {
      todaySectionEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } else {
      goToToday(); // updates interval with start as today
    }
  }, [goToToday, todaySectionEl]);

  const isWeekView = view === CalendarViewType.Week;
  const isListView = view === CalendarViewType.List;
  const isMonthView = view === CalendarViewType.Month;

  const { startDateString, endDateString } = exclusiveToInclusiveRange(
    visibleRangeStart,
    visibleRangeExclusiveEnd,
    view,
  );

  const { events, isLoading, isPreviousData } = useComplianceCalendarEvents({
    projectId: project?.id,
    startDate: startDateString,
    endDate: endDateString,
    config: { keepPreviousData: true, staleTime: 1000 * 30 },
  });

  const hasOutrunFetchBuffer = useHasOutrunFetchBuffer({
    endDateString,
    isLoading,
    isPreviousData,
    startDateString,
    visibleRangeExclusiveEnd,
    visibleRangeStart,
  });

  const filteredEvents = useMemo(
    () =>
      events &&
      filterCalendarEvents(events, filters).map((event) =>
        isEventClickable(event.status) ? event : { ...event, url: '' },
      ),
    [filters, events],
  );

  // Keep completed events in the day summary totals when "hide completed" is on.
  const summaryEvents = useMemo(
    () =>
      events &&
      filterCalendarEvents(events, { ...filters, hideCompleted: false }),
    [filters, events],
  );

  // Delay only the initial fetch; show catch-up skeletons immediately.
  const showSkeletons = useIsLoadingSlow(isLoading) || hasOutrunFetchBuffer;

  const navigateToEventUrl = useCallback(
    (url: string) => {
      // backend returns the whole url and navigation only needs the path
      const urlObject = new URL(url);
      const urlWithoutHost = urlObject.pathname + urlObject.search;
      const isBackboneUrl = !urlObject.pathname.includes('/vortex/');

      // Inspections open a legacy Backbone page with no SPA route; a
      // react-router navigate would 404, so do a full-page navigation.
      if (isBackboneUrl) {
        navigateWithReferrer(urlWithoutHost);
        return;
      }

      // replaces task paths persisted before the route was renamed to /calendar;
      // (?=\?|$) matches a trailing /tasks segment, with or without a query
      // string; does not match something like /tasks/123
      navigate(urlWithoutHost.replace(/\/tasks(?=\?|$)/, '/calendar'), {
        replace: true,
      });
    },
    [navigate],
  );

  // eventDataTransform is incorrectly typed for our use case, even though our use case is officially supported
  // https://github.com/fullcalendar/fullcalendar/issues/5534#issuecomment-649816909
  const convertEventsAvoidingTypeProblems = useCallback(
    (backendEvent: EventInput) =>
      convertEventToFullCalendarFormat(backendEvent as ComplianceCalendarEvent),
    [],
  );

  const getEventContent = useCallback(
    (eventInfo: EventContentArg) => {
      if (eventInfo.event.extendedProps.isLoadingIndicator) {
        return (
          <>
            <VisuallyHidden>Loading Event...</VisuallyHidden>
            <StyledSkeleton variant="rect" />
          </>
        );
      }

      const event = {
        status: eventInfo.event.extendedProps
          .status as ComplianceCalendarEventStatus,
        title: eventInfo.event.title,
        type: eventInfo.event.extendedProps.type as ComplianceCalendarEventType,
        url: eventInfo.event.extendedProps
          .url as ComplianceCalendarEvent['url'],
      };

      return (
        <CalendarEventTooltip event={event}>
          {(titleRef) => (
            <MonthViewEventLink
              {...getEventLinkProps(event, navigateToEventUrl)}
            >
              <CalendarEvent
                status={event.status}
                title={event.title}
                titleRef={titleRef}
                truncation="horizontal"
              />
            </MonthViewEventLink>
          )}
        </CalendarEventTooltip>
      );
    },
    [navigateToEventUrl],
  );

  const calendarRange = useMemo(() => getCalendarRange(), []);

  const loadingEvents = useMemo(
    () => createLoadingEvents(visibleRangeStart, visibleRangeExclusiveEnd),
    [visibleRangeStart, visibleRangeExclusiveEnd],
  );

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  function renderByViewType() {
    if (isWeekView || isListView) {
      if (isWeekView) {
        return (
          <ComplianceCalendarWeekView
            events={filteredEvents ?? []}
            exclusiveEnd={visibleRangeExclusiveEnd}
            isLoading={showSkeletons}
            navigateToEventUrl={navigateToEventUrl}
            rangeStart={visibleRangeStart}
            summaryEvents={summaryEvents ?? []}
          />
        );
      }

      return (
        <ComplianceCalendarListView
          events={filteredEvents ?? []}
          exclusiveEnd={visibleRangeExclusiveEnd}
          // Don't use showSkeletons (useIsLoadingSlow) — empty TextBlock would
          // flash during the delay before skeletons kick in.
          isLoading={isLoading || hasOutrunFetchBuffer}
          navigateToEventUrl={navigateToEventUrl}
          rangeStart={visibleRangeStart}
          scrollContainerRef={listScrollRef}
          setTodaySectionRef={setTodaySectionRef}
          summaryEvents={summaryEvents ?? []}
        />
      );
    }

    return (
      <ComplianceCalendarMonthView
        calendarRange={calendarRange}
        eventDataTransform={convertEventsAvoidingTypeProblems}
        events={filteredEvents}
        isLoading={showSkeletons}
        loadingEvents={loadingEvents}
        renderEventContent={getEventContent}
      />
    );
  }

  return (
    <CalendarRoot $scrollsWithPage={isMonthView}>
      <SkipLink linkText="Skip calendar" />
      <ComplianceCalendarHeader
        isTodaySectionInViewport={isTodaySectionInViewport}
        onGoToTodayInList={goToTodayInList}
      />
      {renderByViewType()}

      <SkipLinkTarget />
    </CalendarRoot>
  );
}

export function ComplianceCalendar() {
  return (
    <ErrorBoundary Fallback={ComplianceCalendarErrorFallback}>
      <ComplianceCalendarProvider>
        <ComplianceCalendarContent />
      </ComplianceCalendarProvider>
    </ErrorBoundary>
  );
}
